import { useEffect, useState } from "react";
import { Megaphone } from "lucide-react";
import { api } from "@/lib/api";

// Each tone is a louder skin than the mission strip's, so a notice is never mistaken for the
// standing line. The band is the same shape in both places; only the skin changes.
const TONES = {
  gold: { wrap: "border-y border-[#d4a017]/60 bg-[#d4a017]/[0.14]", badge: "bg-[#d4a017] text-black", text: "text-[#f0c040]" },
  crimson: { wrap: "border-y border-[#e0245e]/60 bg-[#e0245e]/[0.14]", badge: "bg-[#e0245e] text-white", text: "text-[#ff6a92]" },
  emerald: { wrap: "border-y border-[#1db954]/60 bg-[#1db954]/[0.13]", badge: "bg-[#1db954] text-black", text: "text-[#4fdc84]" },
  azure: { wrap: "border-y border-[#1d9bf0]/60 bg-[#1d9bf0]/[0.13]", badge: "bg-[#1d9bf0] text-white", text: "text-[#6ec1ff]" },
  violet: { wrap: "border-y border-[#8b5cf6]/60 bg-[#8b5cf6]/[0.14]", badge: "bg-[#8b5cf6] text-white", text: "text-[#b794ff]" },
};

const Track = ({ notice, skin, hidden }) => (
  <div
    aria-hidden={hidden || undefined}
    className="marquee-track flex min-w-full shrink-0 items-center justify-around gap-16 whitespace-nowrap py-1.5"
  >
    {[0, 1].map((i) => (
      <span key={i} className="flex items-center gap-3">
        <Megaphone className={`h-3.5 w-3.5 ${skin.text}`} />
        <span
          className={`font-display text-[12px] font-black uppercase tracking-[0.18em] ${skin.text}`}
        >
          {notice.message}
        </span>
      </span>
    ))}
  </div>
);

/**
 * The notice board's band — the second scrolling strip, beside the standing mission line.
 *
 * What it shows is the newest notice a super-admin has switched on for this place. Nothing at all
 * is drawn when none is switched on, so the portal never carries an empty band.
 */
export const NoticeBand = ({ where = "portal", refreshKey = 0 }) => {
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    api
      .get(where === "login" ? "/board/live" : "/board/live/portal")
      .then((r) => setNotice(r.data.notice))
      .catch(() => setNotice(null));
  }, [where, refreshKey]);

  if (!notice?.message) return null;
  const skin = TONES[notice.tone] || TONES.gold;

  const band = (
    <div
      data-testid={`notice-band-${where}`}
      title={notice.message}
      className={`marquee-wrap no-print flex items-stretch ${skin.wrap}`}
    >
      <span
        className={`flex shrink-0 items-center gap-2 px-3 font-display text-[10px] font-black uppercase tracking-[0.2em] ${skin.badge}`}
      >
        <span className="mission-flash h-1.5 w-1.5 rounded-full bg-current" />
        Notice
      </span>
      <div className="relative flex min-w-0 flex-1 overflow-hidden">
        <Track notice={notice} skin={skin} />
        <Track notice={notice} skin={skin} hidden />
      </div>
    </div>
  );

  return notice.link ? (
    <a href={notice.link} target="_blank" rel="noreferrer" data-testid={`notice-link-${where}`}>
      {band}
    </a>
  ) : (
    band
  );
};
